import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { config } from '../../../config/config';
import { CookieEnum } from '../../../enums/cookie-enum';
import {
  IForgotPasswordRequest,
  IForgotPasswordResponse,
  IInviteTokenRequest,
  IInviteTokenResponse,
  ILoginRequest,
  ILoginResponse,
  IRegistrationRequest,
  IRegistrationResponse,
  IResendVerifyCodeRequest,
  IResendVerifyCodeResponse,
  IUpdatePasswordRequest,
  IUpdatePasswordResponse,
} from '../../../interfaces/auth.interface';
import {
  IInviteUsersEmailsRequest,
  IInviteUsersEmailsResponse,
} from '../../../interfaces/user.interface';

const baseQuery = fetchBaseQuery({
  baseUrl: config.API_BASE_URL + '/auth',
});

const baseQueryWithInterceptor = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  const response = await baseQuery(args, api, extraOptions);

  if ((response as any)?.error?.status === 401) {
    Cookies.remove(CookieEnum.ACCESS_TOKEN);
  }

  return response;
};

const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithInterceptor,
  tagTypes: ['auth'],

  endpoints: (builder) => ({
    getInviteToken: builder.query<IInviteTokenResponse, IInviteTokenRequest>({
      query: ({ inviteToken }: IInviteTokenRequest) =>
        `/invite-token-info?inviteToken=${inviteToken}`,
    }),
    registration: builder.mutation<
      IRegistrationResponse,
      {
        inviteToken: string;
        registrationData: IRegistrationRequest;
      }
    >({
      query: (data: {
        inviteToken: string;
        registrationData: IRegistrationRequest;
      }) => ({
        url: '/registration',
        method: 'POST',
        body: data.registrationData,
        headers: {
          Authorization: `Bearer ${data.inviteToken}`,
        },
      }),
      invalidatesTags: ['auth'],
    }),
    verifyCode: builder.mutation<
      ILoginResponse,
      { email: string; verificationCode: string; password: string }
    >({
      query: (data: {
        email: string;
        verificationCode: string;
        password: string;
      }) => ({
        url: `/verify-code`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['auth'],
    }),
    login: builder.mutation<ILoginResponse, ILoginRequest>({
      query: (data: ILoginRequest) => ({
        url: `/login`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['auth'],
    }),
    resendVerifyCode: builder.mutation<
      IResendVerifyCodeResponse,
      IResendVerifyCodeRequest
    >({
      query: (data: IResendVerifyCodeRequest) => ({
        url: `/resend-verify-code`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['auth'],
    }),
    forgotPassword: builder.mutation<
      IForgotPasswordResponse,
      IForgotPasswordRequest
    >({
      query: (data: IForgotPasswordRequest) => ({
        url: `/forgot-password`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['auth'],
    }),
    updatePassword: builder.mutation<
      IUpdatePasswordResponse,
      IUpdatePasswordRequest
    >({
      query: (data: IUpdatePasswordRequest) => ({
        url: `/update-password`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['auth'],
    }),

    inviteUsersByEmails: builder.mutation<
      IInviteUsersEmailsResponse,
      IInviteUsersEmailsRequest
    >({
      query: (data: IInviteUsersEmailsRequest) => {
        const token = Cookies.get(CookieEnum.ACCESS_TOKEN);
        return {
          url: `/invite-users`,
          method: 'POST',
          body: data,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      invalidatesTags: ['auth'],
    }),
    deleteInviteUserByEmail: builder.mutation<
      IInviteUsersEmailsResponse,
      string
    >({
      query: (email: string) => {
        const token = Cookies.get(CookieEnum.ACCESS_TOKEN);
        return {
          url: `/invite-user/email/${email}`,
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      invalidatesTags: ['auth'],
    }),
  }),

  refetchOnReconnect: true,
});

export const {
  useGetInviteTokenQuery,
  useRegistrationMutation,
  useVerifyCodeMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useUpdatePasswordMutation,
  useInviteUsersByEmailsMutation,
  useResendVerifyCodeMutation,
  useDeleteInviteUserByEmailMutation,
  usePrefetch,
} = authApi;

export default authApi;
